import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { joinedNativeObservation, nativeObservationKey } from "mobile/nativeBreakoutObservation"
import { nativeCall, type NativeCallState } from "mobile/nativeBridge"
import { getAuthGeneration } from "utils/auth"
import { meetings } from "services/api/meetingsClient"
import type { GuestMeetingJoinResponse, MeetingResponse } from "services/api/meetingsClient"
import type { JoinInfo, LoadState } from "../types"
import { readStoredGuestSession, resolveGuestSession, saveStoredGuestSession } from "../shared/guestSession"
import { AUTHENTICATED_GUEST_FALLBACK_MESSAGE, isAuthenticatedGuestFallbackError } from "../shared/meetingAccess"

type UseMeetingJoinFlowArgs = {
  meetingId?: string
  guestToken: string | null
  isAuthLoading: boolean
  effectiveIsAuthenticated: boolean
  userId?: string | number
  nativeRecovery?: boolean
}

/**
 * Renderer-agnostic meeting entry: authenticated loading, guest-link choice/form,
 * stored guest session rejoin, and the load-state machine shown before a call surface mounts.
 */
export function useMeetingJoinFlow({
  meetingId,
  guestToken,
  isAuthLoading,
  effectiveIsAuthenticated,
  nativeRecovery = false,
  userId,
}: UseMeetingJoinFlowArgs) {
  const navigate = useNavigate()
  const location = useLocation()
  const [joinInfo, setJoinInfo] = useState<JoinInfo | null>(null)
  const [meeting, setMeeting] = useState<MeetingResponse | null>(null)
  const [guestMeeting, setGuestMeeting] = useState<GuestMeetingJoinResponse["meeting"] | null>(null)
  const [loadState, setLoadState] = useState<LoadState>("loading")
  const [error, setError] = useState<string | null>(null)
  const [guestFallbackAvailable, setGuestFallbackAvailable] = useState(false)
  const [displayName, setDisplayName] = useState("")
  const loadStateRef = useRef(loadState)
  const guestAutoJoinKeyRef = useRef<string | null>(null)
  const guestSessionIdRef = useRef<string | null>(null)
  const guestBreakoutTokenRef = useRef<string | null>(null)
  const routeScope = useMemo(() => ({ meetingId, guestToken }), [meetingId, guestToken])
  const currentRouteRef = useRef(routeScope)
  const resetRouteRef = useRef(routeScope)
  currentRouteRef.current = routeScope
  const requestSequenceRef = useRef(0)

  const authGeneration = getAuthGeneration()
  const entryScope = useMemo(() => ({ routeScope, effectiveIsAuthenticated, authGeneration, userId }), [routeScope, effectiveIsAuthenticated, authGeneration, userId])
  const currentEntryScope = useRef(entryScope); currentEntryScope.current = entryScope
  const mountedEntry = useRef(true)
  useEffect(() => { mountedEntry.current = true; return () => { mountedEntry.current = false; requestSequenceRef.current += 1 } }, [])
  const entryDecision = useRef<{ scope: typeof entryScope; mode: 'ordinary' | 'retained' } | null>(null)
  const ordinaryStarted = useRef<typeof entryScope | null>(null)
  const [toolsBlocked, setToolsBlocked] = useState(false)
  const [toolsRetry, setToolsRetry] = useState(0)
  const [toolsState, setToolsState] = useState<'loading' | 'ready' | 'degraded' | 'denied'>('loading')
  const retryTools = useCallback(() => setToolsRetry(value => value + 1), [])
  const [nativeRetry, setNativeRetry] = useState(0)
  const [nativeEntry, setNativeEntry] = useState<{ scope: typeof entryScope; retained: boolean } | null>(null)
  const [otherNativeMeeting, setOtherNativeMeeting] = useState<string | null>(null)
  const [nativePeer, setNativePeer] = useState<NativeCallState | null>(null)
  const nativePeerRef = useRef(nativePeer); nativePeerRef.current = nativePeer
  const credentialScope = useMemo(() => ({ routeScope, effectiveIsAuthenticated, authGeneration, userId }), [routeScope, effectiveIsAuthenticated, authGeneration, userId])
  const acceptedEntry = useRef<typeof credentialScope | null>(null)
  useEffect(() => {
    if (!nativeRecovery || isAuthLoading || !meetingId) return
    let current = true
    let events = 0
    let handle: { remove: () => Promise<void> } | undefined
    const owns = () => current && currentEntryScope.current === entryScope && getAuthGeneration() === authGeneration
    const unknown = () => {
      if (!owns()) return
      // Keep the known scoped surface mounted. Without evidence, show recovery;
      // never interpret a failed/malformed read as idle or ordinary entry.
      if (nativePeerRef.current?.meetingId === meetingId) return
      setError('Unable to verify the active native call. Try again or wait for the call to reconnect.')
      setLoadState('error')
    }
    const observe = (peer: NativeCallState) => {
      if (!owns() || (entryDecision.current?.scope === entryScope && entryDecision.current.mode === 'ordinary')) return
      if (!peer || !['idle', 'preview', 'joining', 'joined', 'waiting', 'left', 'denied', 'error'].includes(peer.state)) { unknown(); return }
      if (peer.meetingId && peer.meetingId !== meetingId && ['joining', 'joined', 'preview', 'waiting'].includes(peer.state)) { setOtherNativeMeeting(peer.meetingId); unknown(); return }
      setOtherNativeMeeting(null)
      const previous = nativePeerRef.current
      if (previous?.generation !== undefined && (peer.generation === undefined || peer.generation < previous.generation)) return
      const retained = peer.meetingId?.toLowerCase() === meetingId.toLowerCase() && ['joining', 'joined'].includes(peer.state)
      if (retained && (!peer.accessId || !peer.operationId || !Number.isSafeInteger(peer.generation) || (peer.state === 'joined' && peer.accessVerifying !== true && !joinedNativeObservation(peer, meetingId)))) { unknown(); return }
      if (retained || (entryDecision.current?.scope === entryScope && entryDecision.current.mode === 'retained' && previous?.meetingId === meetingId)) {
        if (entryDecision.current?.scope !== entryScope || entryDecision.current.mode !== 'retained') {
          requestSequenceRef.current += 1
          entryDecision.current = { scope: entryScope, mode: 'retained' }
        }
        nativePeerRef.current = peer; setNativePeer({ ...peer })
        if (retained) {
          const proof = joinedNativeObservation(peer, meetingId)
          const sameSubject = acceptedEntry.current === credentialScope
          const guestPeer = proof ? proof.userId.startsWith('g') : sameSubject && Boolean(guestSessionIdRef.current)
          const stored = guestToken ? readStoredGuestSession(meetingId, guestToken) : null
          const guest = guestPeer && stored && (!proof || proof.userId === `g${stored.guestSessionId.replace(/-/g, '')}`) ? stored : null
          guestSessionIdRef.current = guest?.guestSessionId || null
          guestBreakoutTokenRef.current = guest?.breakoutToken || null
          const proofMatchesSubject = !proof || (guestPeer ? Boolean(guest) : proof.userId === String(userId))
          const keepTools = acceptedEntry.current === credentialScope && previous?.accessId === peer.accessId && proofMatchesSubject
          setJoinInfo(old => ({ ...(keepTools ? old : null), url: keepTools ? old?.url || '' : '', token: null,
            native_calls_enabled: true, retained_native: true, retained_native_guest: guestPeer,
            native_entry_snapshot: peer,
            ...(guest ? { guest_session_id: guest.guestSessionId, breakout_token: guest.breakoutToken } : {}) }))
          if (!keepTools) { setMeeting(null); setGuestMeeting(null); setDisplayName('') }
          acceptedEntry.current = credentialScope
          setError(null); setLoadState('ready')
        }
        setNativeEntry({ scope: entryScope, retained: true })
        return
      }
      // Terminal states hold no call, whichever meeting they last described. Android
      // reports `left` with no meeting before its first call.
      if (!['idle', 'left', 'denied', 'error'].includes(peer.state)) { unknown(); return }
      entryDecision.current = { scope: entryScope, mode: 'ordinary' }
      setNativeEntry({ scope: entryScope, retained: false })
    }
    const timer = window.setTimeout(unknown, 8000)
    void nativeCall.listen(peer => { events += 1; observe(peer) }).then(value => {
      if (!current) { void value.remove(); return }
      handle = value
      const revision = events
      return nativeCall.getState().then(peer => { if (events === revision) observe(peer) })
    }).catch(unknown).finally(() => window.clearTimeout(timer))
    return () => { current = false; window.clearTimeout(timer); void handle?.remove() }
  }, [nativeRecovery, nativeRetry, isAuthLoading, meetingId, guestToken, entryScope, authGeneration, credentialScope, userId])

  const peerKey = nativePeer ? nativeObservationKey(nativePeer) : ''
  useEffect(() => {
    if (!nativeRecovery || !meetingId || isAuthLoading || !nativePeer) return
    const peer = joinedNativeObservation(nativePeer, meetingId)
    if (!peer) return
    const stored = guestToken ? readStoredGuestSession(meetingId, guestToken) : null
    const isGuest = peer.userId.startsWith('g')
    if (isGuest ? !stored?.breakoutToken || peer.userId !== `g${stored.guestSessionId.replace(/-/g, '')}` : peer.userId !== String(userId)) {
      setToolsState('denied'); setToolsBlocked(true)
      return
    }
    setToolsBlocked(false)
    let current = true
    let timer: ReturnType<typeof setTimeout>
    let deadline: ReturnType<typeof setTimeout>
    let attempt = 0
    setToolsState(current => current === 'ready' ? current : 'loading')
    const owns = () => current && currentEntryScope.current === entryScope && getAuthGeneration() === authGeneration
      && nativePeerRef.current && nativeObservationKey(nativePeerRef.current) === peerKey
    const renew = async () => {
      try {
        const result = await Promise.race([
          meetings.renewNativeCallTools(meetingId, { access_id: peer.accessId, observed_room_url: peer.roomUrl,
            ...(isGuest ? { breakout_token: stored!.breakoutToken, guest_token: guestToken! } : {}) }),
          new Promise<never>((_, reject) => { deadline = setTimeout(() => reject(new Error('Tool renewal timeout')), 8000) }),
        ])
        if (!owns()) return
        if (result.access_id !== peer.accessId || result.subject !== (isGuest ? `guest:${stored!.guestSessionId}` : `user:${peer.userId}`)) throw Object.assign(new Error('Tool context mismatch'), { code: 'NATIVE_CONTEXT_MISMATCH' })
        setToolsState('ready')
        setJoinInfo({ ...result.join_info, native_entry_snapshot: nativePeerRef.current! })
        setDisplayName(result.join_info.display_name || '')
        if (isGuest) { setGuestMeeting(result.meeting); setMeeting(null) } else { setMeeting(result.meeting); setGuestMeeting(null) }
        attempt = 0
        timer = setTimeout(() => void renew(), 180000)
      } catch (failure: any) {
        if (!owns()) return
        if (failure?.code === 'NATIVE_CONTEXT_MISMATCH' || [401, 403, 404, 410].includes(failure?.response?.status)) {
          setToolsState('denied')
          // Revocation removes tool credentials, never native scoped Leave.
          setJoinInfo(old => old ? { url: old.url, token: null, native_calls_enabled: true, retained_native: true,
            retained_native_guest: isGuest, native_entry_snapshot: nativePeerRef.current! } : old)
          setMeeting(null); setGuestMeeting(null)
        } else { setToolsState('degraded'); timer = setTimeout(() => void renew(), Math.min(30000, 2000 * 2 ** Math.min(attempt++, 4))) }
      } finally { clearTimeout(deadline) }
    }
    void renew()
    return () => { current = false; clearTimeout(timer); clearTimeout(deadline) }
  // Peer metadata churn must not continuously renew credentials.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nativeRecovery, meetingId, guestToken, isAuthLoading, userId, entryScope, authGeneration, peerKey, nativeRetry, toolsRetry])

  useEffect(() => {
    if (!joinInfo?.native_tools_expires_at) return
    const timer = setTimeout(() => {
      setToolsState('degraded')
      setJoinInfo(old => old ? { url: old.url, token: null, native_calls_enabled: true, retained_native: true,
        retained_native_guest: old.retained_native_guest, native_entry_snapshot: old.native_entry_snapshot } : old)
    }, Math.max(0, Date.parse(joinInfo.native_tools_expires_at) - Date.now()))
    return () => clearTimeout(timer)
  }, [joinInfo?.native_tools_expires_at, credentialScope])

  useEffect(() => {
    loadStateRef.current = loadState
  }, [loadState])

  const goToLogin = useCallback(() => {
    navigate("/login", { state: { from: location }, replace: true })
  }, [location, navigate])

  const loadMeeting = useCallback(async () => {
    const request = ++requestSequenceRef.current
    const isCurrent = () => mountedEntry.current && currentEntryScope.current === entryScope && getAuthGeneration() === authGeneration
      && currentRouteRef.current === routeScope && requestSequenceRef.current === request
      && (!nativeRecovery || (entryDecision.current?.scope === entryScope && entryDecision.current.mode === 'ordinary'))
    if (!meetingId) {
      setError("No meeting id provided.")
      setLoadState("error")
      return
    }

    setLoadState("loading")
    setError(null)
    setGuestFallbackAvailable(false)

    try {
      const [meetingResp, joinResp] = await Promise.all([
        meetings.getMeeting(meetingId),
        meetings.joinMeeting(meetingId),
      ])
      if (!isCurrent()) return
      acceptedEntry.current = credentialScope
      setMeeting(meetingResp)
      setGuestMeeting(null)
      setJoinInfo(joinResp)
      setLoadState("ready")
    } catch (err: any) {
      if (!isCurrent()) return
      acceptedEntry.current = credentialScope
      if (guestToken && isAuthenticatedGuestFallbackError(err)) {
        setMeeting(null)
        setGuestMeeting(null)
        setJoinInfo(null)
        setGuestFallbackAvailable(true)
        setError(AUTHENTICATED_GUEST_FALLBACK_MESSAGE)
        setLoadState("guest-choice")
        return
      }
      const message = err?.response?.data?.detail || err?.message || "Unable to load meeting."
      setError(message)
      setLoadState("error")
    }
  }, [guestToken, meetingId, routeScope, entryScope, authGeneration, nativeRecovery, credentialScope])

  const showGuestForm = useCallback(() => {
    if (!meetingId || !guestToken) {
      goToLogin()
      return
    }
    const storedSession = readStoredGuestSession(meetingId, guestToken)
    if (storedSession?.displayName) {
      setDisplayName(storedSession.displayName)
    }
    setError(null)
    setGuestMeeting(null)
    setLoadState("guest-form")
  }, [goToLogin, guestToken, meetingId])

  const joinAsGuest = useCallback(async (requestedDisplayName?: string, preferredSessionId?: string | null) => {
    const request = ++requestSequenceRef.current
    const isCurrent = () => mountedEntry.current && currentEntryScope.current === entryScope && getAuthGeneration() === authGeneration
      && currentRouteRef.current === routeScope && requestSequenceRef.current === request
      && (!nativeRecovery || (entryDecision.current?.scope === entryScope && entryDecision.current.mode === 'ordinary'))
    if (!meetingId || !guestToken) {
      goToLogin()
      return
    }
    const guestSession = resolveGuestSession(
      meetingId,
      guestToken,
      requestedDisplayName || displayName,
      preferredSessionId,
    )
    setLoadState("joining")
    setError(null)
    setGuestMeeting(null)
    try {
      const response = await meetings.joinGuestMeeting(meetingId, {
        guest_token: guestToken,
        display_name: guestSession.displayName,
        guest_session_id: guestSession.guestSessionId,
        ...(guestSession.breakoutToken ? { breakout_token: guestSession.breakoutToken } : {}),
      })
      if (!isCurrent()) return
      acceptedEntry.current = credentialScope
      const confirmedSession = {
        displayName: response.display_name || guestSession.displayName,
        guestSessionId: response.guest_session_id || guestSession.guestSessionId,
        breakoutToken: response.breakout_token || undefined,
      }
      saveStoredGuestSession(meetingId, guestToken, confirmedSession)
      guestSessionIdRef.current = confirmedSession.guestSessionId
      guestBreakoutTokenRef.current = response.breakout_token || null
      guestAutoJoinKeyRef.current = `${meetingId}:${guestToken}:${confirmedSession.guestSessionId}`
      setDisplayName(confirmedSession.displayName)
      setJoinInfo({
        url: response.url,
        token: response.token,
        requires_admission: response.requires_admission,
        native_calls_enabled: response.native_calls_enabled,
        waiting_room_enabled: response.waiting_room_enabled,
        waiting_room_unlocked: response.waiting_room_unlocked,
        can_unlock_wait_room: response.can_unlock_wait_room,
        display_name: confirmedSession.displayName,
        guest_session_id: confirmedSession.guestSessionId,
        breakout_token: response.breakout_token,
        questions_panel: response.questions_panel,
        messages_panel: response.messages_panel,
        inspire_panel: response.inspire_panel,
        poll_panel: response.poll_panel,
      })
      setGuestMeeting(response.meeting)
      setMeeting(null)
      setLoadState("ready")
    } catch (err: any) {
      if (!isCurrent()) return
      acceptedEntry.current = credentialScope
      if (err?.response?.status === 403 && guestSession.breakoutToken) {
        // The rejected proof cannot resume this identity. A reopened link starts
        // safely with a newly assigned identity rather than retrying stale proof.
        saveStoredGuestSession(meetingId, guestToken, { displayName: guestSession.displayName, guestSessionId: guestSession.guestSessionId })
      }
      const message = err?.response?.data?.detail || err?.message || "Unable to join this meeting."
      setError(message)
      setLoadState("error")
    }
  }, [displayName, goToLogin, guestToken, meetingId, routeScope, entryScope, authGeneration, nativeRecovery, credentialScope])

  const handleGuestJoin = useCallback(async (event: FormEvent) => {
    event.preventDefault()
    await joinAsGuest(displayName)
  }, [displayName, joinAsGuest])

  const handleRetry = useCallback(() => {
    if (nativeRecovery && entryDecision.current?.mode !== 'ordinary') { setNativeRetry(value => value + 1); return }
    if (guestToken && guestFallbackAvailable) {
      showGuestForm()
      return
    }
    if (effectiveIsAuthenticated) {
      loadMeeting()
      return
    }
    showGuestForm()
  }, [nativeRecovery, effectiveIsAuthenticated, guestFallbackAvailable, guestToken, loadMeeting, showGuestForm])

  useEffect(() => {
    if (resetRouteRef.current === routeScope) return
    resetRouteRef.current = routeScope
    setGuestFallbackAvailable(false)
    setJoinInfo(null)
    setMeeting(null)
    setGuestMeeting(null)
    setDisplayName("")
    setError(null)
    setLoadState("loading")
    loadStateRef.current = "loading"
    guestSessionIdRef.current = null
    guestBreakoutTokenRef.current = null
    guestAutoJoinKeyRef.current = null
  }, [routeScope])

  useEffect(() => {
    if (nativeRecovery && (nativeEntry?.scope !== entryScope || nativeEntry.retained)) return
    if (isAuthLoading) {
      if (nativeRecovery) return
      setLoadState("loading")
      return
    }
    if (guestToken && guestFallbackAvailable) {
      return
    }
    if (nativeRecovery && ordinaryStarted.current === entryScope) return
    if (nativeRecovery) ordinaryStarted.current = entryScope
    if (effectiveIsAuthenticated) {
      loadMeeting()
      return
    }
    if (guestToken) {
      const storedSession = readStoredGuestSession(meetingId, guestToken)
      const autoJoinKey = storedSession ? `${meetingId}:${guestToken}:${storedSession.guestSessionId}` : null
      if (storedSession && guestAutoJoinKeyRef.current !== autoJoinKey) {
        guestAutoJoinKeyRef.current = autoJoinKey
        setDisplayName(storedSession.displayName)
        joinAsGuest(storedSession.displayName, storedSession.guestSessionId)
        return
      }
      if (storedSession) {
        return
      }
      if (loadStateRef.current === "loading") {
        setError(null)
        setLoadState("guest-choice")
      }
      return
    }
    goToLogin()
  }, [
    nativeRecovery, nativeEntry, entryScope,
    effectiveIsAuthenticated,
    goToLogin,
    guestFallbackAvailable,
    guestToken,
    isAuthLoading,
    joinAsGuest,
    loadMeeting,
    meetingId,
  ])

  const visibleScope = !nativeRecovery || acceptedEntry.current === credentialScope
  const visibleJoinInfo = visibleScope ? joinInfo : joinInfo ? { url: '', token: null, native_calls_enabled: true,
    retained_native: true, retained_native_guest: joinInfo.retained_native_guest, native_entry_snapshot: joinInfo.native_entry_snapshot } : null
  return {
    toolsState, toolsBlocked, retryTools,
    otherNativeMeeting,
    joinInfo: visibleJoinInfo,
    setJoinInfo,
    meeting: visibleScope ? meeting : null,
    setMeeting,
    guestMeeting: visibleScope ? guestMeeting : null,
    loadState,
    setLoadState,
    error,
    setError,
    displayName,
    setDisplayName,
    guestFallbackAvailable,
    guestSessionIdRef,
    guestBreakoutTokenRef,
    goToLogin,
    showGuestForm,
    handleGuestJoin,
    handleRetry,
  }
}
